import type {
  CampaignActivityEntry as ApiActivityEntry,
  CampaignActivityGroup as ApiActivityGroup,
  CampaignAudience as ApiAudience,
  CampaignContactRow as ApiContactRow,
  CampaignDeliveryBreakdown as ApiDeliveryBreakdown,
  CampaignDetailHeader as ApiDetailHeader,
  CampaignIndexRow as ApiIndexRow,
  CampaignMetric as ApiMetric,
  CampaignOverviewStep as ApiOverviewStep,
  CampaignOwnerRead as ApiOwner,
  CampaignRailRow as ApiRailRow,
  CampaignSequenceStep as ApiSequenceStep,
  CampaignSequenceStepStat as ApiSequenceStepStat,
  CampaignSequenceSwitch as ApiSequenceSwitch,
  CampaignStatValue as ApiStatValue,
  CampaignTone,
} from '#/shared/api/generated/communication/models'

import type {
  CampaignMetric,
  CampaignOwner,
  CampaignRow,
} from '../model/campaign'
import type {
  CampaignActivityEntry,
  CampaignActivityGroup,
} from '../model/campaign-activity'
import type { CampaignContactRow } from '../model/campaign-contact'
import type {
  CampaignAudience,
  CampaignDeliveryBreakdown,
  CampaignDetailHeader,
  CampaignRailRow,
  CampaignStep,
} from '../model/campaign-detail'
import type {
  CampaignSequenceStep,
  CampaignSequenceSwitch,
  CampaignStepStat,
} from '../model/campaign-sequence'

/**
 * The campaign service already speaks the UI's language — every figure arrives
 * formatted and every tone is a server decision — so this layer only renames
 * `snake_case` to the view models' `camelCase` and folds `null` into the
 * optional fields the components read. Nothing here derives a number.
 */

/** A formatted figure whose absence is meaningful: keep `null`, drop `undefined`. */
function optional(value: string | null | undefined): string | undefined {
  return value ?? undefined
}

function toCampaignMetric(metric: ApiMetric): CampaignMetric {
  return { value: metric.value ?? null, note: optional(metric.note) }
}

function toCampaignOwner(owner: ApiOwner): CampaignOwner {
  return {
    name: owner.name,
    accent: owner.accent,
    avatarUrl: optional(owner.avatar_url),
  }
}

export function toCampaignRow(row: ApiIndexRow): CampaignRow {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind,
    channels: row.channels,
    audience: row.audience,
    status: row.status,
    people: toCampaignMetric(row.people),
    waitingOnUs: row.waiting_on_us ?? null,
    waitingOverdue: row.waiting_overdue ?? false,
    replies: row.replies ?? null,
    booked: toCampaignMetric(row.booked),
    margin: toCampaignMetric(row.margin),
    owner: toCampaignOwner(row.owner),
    lastSend: row.last_send ?? null,
  }
}

export interface CampaignStatFigure {
  value: string
  note?: string
  /** The ink the service picked for this figure. */
  tone?: CampaignTone
}

export function toCampaignStat(stat: ApiStatValue): CampaignStatFigure {
  return { value: stat.value, note: optional(stat.note), tone: stat.tone }
}

export function toCampaignDetailHeader(
  header: ApiDetailHeader,
): CampaignDetailHeader {
  return {
    id: header.id,
    name: header.name,
    channels: header.channels,
    status: header.status,
    summary: header.summary,
    canPowerDial: header.can_power_dial,
    powerDialCount: header.power_dial_count ?? 0,
  }
}

export function toCampaignStep(step: ApiOverviewStep): CampaignStep {
  return {
    id: step.id,
    position: step.position,
    channel: step.channel,
    timing: step.timing,
    reach: step.reach,
    barTone: step.bar_tone,
    delivery: step.delivery,
    pending: step.pending,
    replied: step.replied,
    booked: step.booked,
    margin: step.margin,
    costPerBooking: step.cost_per_booking,
    exit: optional(step.exit),
  }
}

export function toCampaignDeliveryBreakdown(
  breakdown: ApiDeliveryBreakdown,
): CampaignDeliveryBreakdown {
  return {
    label: breakdown.label,
    actionLabel: breakdown.action_label,
    segments: breakdown.segments,
    note: breakdown.note,
  }
}

export function toCampaignRailRow(row: ApiRailRow): CampaignRailRow {
  return { key: row.key, label: row.label, value: row.value, tone: row.tone }
}

export function toCampaignAudience(audience: ApiAudience): CampaignAudience {
  return {
    enrolled: audience.enrolled,
    // The service does not persist the filter's candidate pool, so it sends
    // null rather than echoing `enrolled` twice; the em-dash is the honest cell.
    matched: audience.matched ?? '—',
    // `audience.chips` is deliberately dropped: it carries the trigger event
    // only, never the rolling filter. The card reads the rule off the
    // campaign itself — see `use-campaign-strategy-chips.ts`.
    note: audience.note,
  }
}

export function toCampaignContactRow(row: ApiContactRow): CampaignContactRow {
  return {
    id: row.id,
    customerId: optional(row.customer_id),
    shipmentId: optional(row.shipment_id),
    name: row.name,
    phone: row.phone,
    email: row.email,
    quote: row.quote,
    tariff: {
      amount: row.tariff.amount,
      status: row.tariff.status,
      statusTone: row.tariff.status_tone,
    },
    step: row.step,
    activity: {
      headline: row.activity.headline,
      failed: row.activity.failed ?? false,
      timestamp: row.activity.timestamp,
      answerState: optional(row.activity.answer_state),
      awaiting: row.activity.awaiting ?? false,
    },
    outcome: row.outcome,
    outcomeTone: row.outcome_tone,
  }
}

function toCampaignActivityEntry(
  entry: ApiActivityEntry,
): CampaignActivityEntry {
  return {
    id: entry.id,
    time: entry.time,
    actor: entry.actor ?? undefined,
    title: entry.title,
    detail: entry.detail,
    tone: entry.tone,
  }
}

export function toCampaignActivityGroup(
  group: ApiActivityGroup,
): CampaignActivityGroup {
  return {
    key: group.key,
    label: group.label,
    entries: group.entries.map(toCampaignActivityEntry),
  }
}

function toCampaignStepStat(stat: ApiSequenceStepStat): CampaignStepStat {
  return { key: stat.key, value: stat.value ?? null, strong: stat.strong }
}

/**
 * The step card, verbatim. `timing`, `state_label`, `footer_note` and the
 * `wait` sentence are the service's words — the one thing worth knowing is
 * that `wait` is never null, so the last step closes the sequence rather than
 * trailing off, and the first stat's key flips to `queued` before a step
 * sends. Both are read straight through.
 */
export function toCampaignSequenceStep(
  step: ApiSequenceStep,
): CampaignSequenceStep {
  return {
    id: step.id,
    position: step.step_index,
    channel: step.channel,
    timing: step.timing,
    state: step.state,
    stateLabel: step.state_label,
    subject: optional(step.subject),
    threadNote: optional(step.thread_note),
    body: step.body,
    footerNote: step.footer_note,
    isActive: step.is_active,
    stats: step.stats.map(toCampaignStepStat),
    wait: { label: step.wait.label, condition: step.wait.condition },
  }
}

export function toCampaignSequenceSwitch(
  row: ApiSequenceSwitch,
): CampaignSequenceSwitch {
  return {
    key: row.key,
    label: row.label,
    note: row.note ?? '',
    enabled: row.enabled,
    // Compliance switches the service refuses to flip — the unsubscribe exit
    // is a legal instruction, quiet hours are carrier policy.
    locked: row.locked,
  }
}
